import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';

import { withReset } from '@nbx/core/state';

import { filter } from 'rxjs';

import { APP_EXTERNAL_PATHS } from '@core/enums/paths/app.paths';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';

export interface BreadcrumbItem {
  label: string;
  route?: string;
}

export type BreadcrumbParams = Record<string, string>;

interface UiStateModel {
  rawBreadcrumbs: BreadcrumbItem[];
  breadcrumbParams: BreadcrumbParams;
  mainScrolled: boolean;
  mainScrollEnabled: boolean;
  isExternalRoute: boolean;
}

const initialState: UiStateModel = {
  rawBreadcrumbs: [],
  breadcrumbParams: {},
  mainScrolled: false,
  mainScrollEnabled: true,
  isExternalRoute: false,
};

function getActiveRouteBranch(router: Router): ActivatedRouteSnapshot[] {
  const routes: ActivatedRouteSnapshot[] = [];
  let route: ActivatedRouteSnapshot | null = router.routerState.snapshot.root;
  while (route) {
    routes.push(route);
    route = route.firstChild;
  }
  return routes;
}

function checkExternalRoute(url: string): boolean {
  return Object.values(APP_EXTERNAL_PATHS).some(path => url.includes(path));
}

/**
 * Reads `data.mainScroll` from the deepest activated route up to the root, returning the first
 * defined value; defaults to `true` when no route defines it.
 */
function resolveMainScroll(router: Router): boolean {
  const routes = getActiveRouteBranch(router);
  for (let i = routes.length - 1; i >= 0; i--) {
    const value = routes[i].data['mainScroll'] as boolean | undefined;
    if (value !== undefined) {
      return !!value;
    }
  }
  return true;
}

/**
 * Collects breadcrumb items from each route's `data.breadcrumb`; URLs are derived from the
 * accumulated route segments so dynamic params (e.g. `:id`) resolve to their real values.
 */
function buildBreadcrumbs(router: Router): BreadcrumbItem[] {
  const crumbs: BreadcrumbItem[] = [];
  const urlParts: string[] = [];
  for (const route of getActiveRouteBranch(router)) {
    for (const seg of route.url) {
      urlParts.push(seg.path);
    }
    const bc = route.routeConfig?.data?.['breadcrumb'] as BreadcrumbItem | undefined;
    if (bc?.label) {
      crumbs.push({ ...bc, route: bc.route ?? `/${urlParts.join('/')}` });
    }
  }
  return crumbs;
}

/**
 * Global UI state driven by the current route: breadcrumb navigation (dynamic segments kept as
 * `:paramName` until resolved via params) and main-scroll enablement (from `data.mainScroll`,
 * default `true`). The route-driven refresh lives in an `onInit` effect whose writes are
 * `untracked` so it never self-loops.
 */
export const UiStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => {
    const router = inject(Router);
    return {
      _router: router,
      _document: inject(DOCUMENT),
      navigationEnd: toSignal(
        router.events.pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd)
        )
      ),
    };
  }),
  withComputed(store => ({
    breadcrumbs: computed(() =>
      store.rawBreadcrumbs().map(crumb => {
        if (!crumb.label.startsWith(':')) {
          return crumb;
        }
        const key = crumb.label.slice(1);
        const resolved = store.breadcrumbParams()[key];
        return resolved ? { ...crumb, label: resolved } : crumb;
      })
    ),
    currentUrl: computed(() => store.navigationEnd()?.urlAfterRedirects ?? store._router.url),
  })),
  withMethods(store => ({
    setBreadcrumbParams(params: BreadcrumbParams): void {
      patchState(store, { breadcrumbParams: params });
    },
    /**
     * Array-based breadcrumb params setter; accepts `[{ key: 'rentalId', value: 'My Villa' }]`
     * (leading colons in keys are stripped).
     */
    loadBreadcrumbParams(params: { key: string; value: string }[]): void {
      const mapped: BreadcrumbParams = {};
      for (const { key, value } of params) {
        mapped[key.startsWith(':') ? key.slice(1) : key] = value;
      }
      patchState(store, { breadcrumbParams: mapped });
    },
    setMainScrolled(scrolled: boolean): void {
      patchState(store, { mainScrolled: scrolled });
    },
    setMainScrollEnabled(enabled: boolean): void {
      patchState(store, { mainScrollEnabled: enabled });
    },
  })),
  withHooks({
    onInit(store) {
      patchState(store, {
        isExternalRoute: checkExternalRoute(store._document.location.pathname),
      });
      effect(() => {
        const event = store.navigationEnd();
        if (!event) {
          return;
        }
        const url = event.urlAfterRedirects;
        untracked(() =>
          patchState(store, {
            breadcrumbParams: {},
            rawBreadcrumbs: buildBreadcrumbs(store._router),
            mainScrollEnabled: resolveMainScroll(store._router),
            isExternalRoute: checkExternalRoute(url),
          })
        );
      });
    },
  }),
  withReset({ global: false })
);
